import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const fmtDate = (v) => {
    if (!v) return "—";
    try {
        const d = new Date(v);
        return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return String(v); }
};

/**
 * PublisherDashboard — read-only self-service view for a single publisher.
 *
 * Data is scoped server-side by `_publisher_check(request)`. The client
 * never sends `publisher_id` filters; the backend derives ownership from
 * the authenticated publisher's `publisher_ids` array plus any manual
 * `owner_publisher_id` overrides. See test_publisher_panel.py::test_A/B
 * for the guarantee that Publisher A never sees Publisher B's data.
 *
 * Numbers shown:
 *   - Per-link: Clicks (Today / 7d / 30d / Lifetime) + Last Click At
 *   - Per-link: Confirmed Conversions + CVR %
 *   - Individual confirmed conversions (masked payment id + timestamp +
 *     currency + confirmed status; NO email / phone / amount / full pay id)
 *
 * Gross INR/USD revenue is deliberately hidden from this surface — the
 * publisher payload from /api/web/publisher/dashboard never returns
 * inr_minor / usd_minor.
 */
export default function PublisherDashboard() {
    const nav = useNavigate();
    const API = process.env.REACT_APP_BACKEND_URL;
    const [me, setMe] = useState(null);
    const [dash, setDash] = useState(null);
    const [conv, setConv] = useState(null);
    const [err, setErr] = useState(null);
    const [tab, setTab] = useState("links");
    const [commission, setCommission] = useState(null);
    const [showLinkForm, setShowLinkForm] = useState(false);

    const load = async () => {
        try {
            const meR = await fetch(`${API}/api/web/publisher/me`, { credentials: "include" });
            if (!meR.ok) { nav("/publisher/login", { replace: true }); return; }
            const meD = await meR.json();
            setMe(meD.publisher);
            const dR = await fetch(`${API}/api/web/publisher/dashboard`, { credentials: "include" });
            if (!dR.ok) throw new Error("Failed to load dashboard");
            setDash(await dR.json());
            const cR = await fetch(`${API}/api/web/publisher/conversions?limit=200`, { credentials: "include" });
            if (cR.ok) setConv(await cR.json());
            const commR = await fetch(`${API}/api/web/publisher/commission`, { credentials: "include" });
            if (commR.ok) setCommission(await commR.json());
        } catch (e) { setErr(e.message); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const logout = async () => {
        await fetch(`${API}/api/web/publisher/auth/logout`, { method: "POST", credentials: "include" });
        nav("/publisher/login", { replace: true });
    };

    if (err) return (
        <div className="min-h-screen bg-femcare-cream/40 flex items-center justify-center p-8">
            <div className="rounded-2xl bg-red-50 text-red-800 px-6 py-4" data-testid="publisher-error">{err}</div>
        </div>
    );
    if (!dash) return (
        <div className="min-h-screen bg-femcare-cream/40 flex items-center justify-center text-femcare-ink/60"
             data-testid="publisher-loading">Loading…</div>
    );

    const t = dash.totals || {};
    return (
        <div className="min-h-screen bg-femcare-cream/40 py-10" data-testid="publisher-dashboard">
            <div className="max-w-6xl mx-auto px-6 space-y-6">
                <header className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-femcare-plum font-semibold">Femcare</div>
                        <h1 className="text-2xl font-bold text-femcare-ink mt-1" data-testid="publisher-name">
                            {(me?.display_name || me?.email || "Publisher") + " · Publisher Panel"}
                        </h1>
                        <p className="text-xs text-femcare-ink/55 mt-1">
                            Your Femcare traffic and confirmed conversions. Only your links are shown.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Excel export — scoped server-side by _publisher_check.
                            The publisher only ever downloads THEIR OWN data
                            (locked in by test_Q_publisher_excel_download_ok_and_scoped). */}
                        <a href={`${API}/api/web/publisher/report.xlsx`}
                           className="rounded-xl bg-femcare-plum text-white text-sm font-semibold px-4 py-2 hover:opacity-90"
                           data-testid="publisher-download-xlsx">
                            Download Excel
                        </a>
                        <button onClick={logout} className="text-xs font-semibold text-femcare-plum hover:underline"
                                data-testid="publisher-logout">Sign out</button>
                    </div>
                </header>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Stat label="Your Links"       value={t.links}           tid="pub-total-links" />
                    <Stat label="Lifetime Clicks"  value={t.clicks_lifetime} tid="pub-total-clicks" />
                    <Stat label="Confirmed"        value={t.confirmed}       tid="pub-total-confirmed" />
                    <Stat label="CVR"              value={`${(t.cvr_pct ?? 0).toFixed(2)}%`} tid="pub-total-cvr" />
                </div>

                {commission && <CommissionCard c={commission} />}

                <div className="flex items-center gap-1 border-b border-femcare-border">
                    <TabBtn active={tab==="links"} onClick={()=>setTab("links")} label="Links" tid="pub-tab-links" />
                    <TabBtn active={tab==="conversions"} onClick={()=>setTab("conversions")} label="Confirmed Conversions" tid="pub-tab-conversions" />
                </div>

                {tab === "links" && (
                    <>
                        <div className="flex items-center justify-end">
                            <button
                                onClick={()=>setShowLinkForm(v=>!v)}
                                className="rounded-xl bg-femcare-plum text-white text-sm font-semibold px-4 py-2 hover:opacity-90"
                                data-testid="pub-create-link-btn">
                                {showLinkForm ? "Close" : "+ Create UTM Link"}
                            </button>
                        </div>
                        {showLinkForm && (
                            <PublisherLinkCreator
                                allowedPublisherIds={me?.publisher_ids || []}
                                onCreated={async ()=>{ setShowLinkForm(false); await load(); }}
                                onError={(m)=>setErr(m)}
                            />
                        )}
                        <LinksTable rows={dash.rows} />
                    </>
                )}
                {tab === "conversions" && <ConversionsTable rows={conv?.rows || []} />}

                <div className="text-xs text-femcare-ink/45 pt-4">
                    Femcare · Numbers refreshed on demand. Confirmed conversions use the same Razorpay-verified rule as Femcare's admin dashboard.
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, tid }) {
    return (
        <div className="rounded-2xl bg-white border border-femcare-border p-5" data-testid={tid}>
            <div className="text-xs uppercase tracking-wider text-femcare-ink/55">{label}</div>
            <div className="text-3xl font-bold text-femcare-plum mt-2">{value ?? 0}</div>
        </div>
    );
}

function TabBtn({ active, onClick, label, tid }) {
    return (
        <button type="button" onClick={onClick} data-testid={tid}
            className={`px-5 py-2.5 text-sm font-semibold border-b-2 -mb-px ${active ? "border-femcare-plum text-femcare-plum" : "border-transparent text-femcare-ink/60 hover:text-femcare-ink"}`}>
            {label}
        </button>
    );
}

function LinksTable({ rows }) {
    if (!rows || rows.length === 0) return (
        <div className="bg-white rounded-2xl border border-femcare-border p-8 text-center text-femcare-ink/50"
             data-testid="pub-links-empty">
            You don't have any links yet. Femcare will assign your links to this account.
        </div>
    );
    return (
        <div className="bg-white rounded-2xl border border-femcare-border overflow-x-auto" data-testid="pub-links-table">
            <table className="w-full text-sm">
                <thead className="bg-femcare-cream/40 text-femcare-ink/70 text-xs uppercase tracking-wider">
                    <tr className="text-left">
                        <th className="px-4 py-3">Link Name</th>
                        <th className="px-4 py-3">Tracking URL</th>
                        <th className="px-4 py-3">Target</th>
                        <th className="px-4 py-3">Today</th>
                        <th className="px-4 py-3">7d</th>
                        <th className="px-4 py-3">30d</th>
                        <th className="px-4 py-3">Lifetime</th>
                        <th className="px-4 py-3">Last Click</th>
                        <th className="px-4 py-3">Confirmed</th>
                        <th className="px-4 py-3">CVR %</th>
                    </tr>
                </thead>
                <tbody>
                {rows.map((r) => (
                    <tr key={r.id} className="border-t border-femcare-border" data-testid={`pub-link-${r.id}`}>
                        <td className="px-4 py-3">
                            <div className="font-semibold text-femcare-plum">{r.link_name || "—"}</div>
                            <div className="text-[10px] text-femcare-ink/55 font-mono">{r.utm_source} · {r.utm_campaign}</div>
                        </td>
                        <td className="px-4 py-3 max-w-[280px]">
                            <div className="text-xs font-mono truncate text-femcare-ink/70" title={r.tracking_url}>{r.tracking_url}</div>
                        </td>
                        <td className="px-4 py-3 text-xs">{r.target_market || "—"}</td>
                        <td className="px-4 py-3 font-semibold">{r.clicks_today ?? 0}</td>
                        <td className="px-4 py-3">{r.clicks_7d ?? 0}</td>
                        <td className="px-4 py-3">{r.clicks_30d ?? 0}</td>
                        <td className="px-4 py-3 font-semibold">{r.clicks_lifetime ?? 0}</td>
                        <td className="px-4 py-3 text-xs">{r.last_click_at ? fmtDate(r.last_click_at) : "—"}</td>
                        <td className="px-4 py-3 font-semibold">{r.confirmed_conversions ?? 0}</td>
                        <td className="px-4 py-3 font-semibold text-femcare-plum">{(r.cvr_pct ?? 0).toFixed(2)}%</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
}

function ConversionsTable({ rows }) {
    if (!rows || rows.length === 0) return (
        <div className="bg-white rounded-2xl border border-femcare-border p-8 text-center text-femcare-ink/50"
             data-testid="pub-conversions-empty">
            No confirmed conversions yet. Only Razorpay-captured and server-verified payments appear here.
        </div>
    );
    return (
        <div className="bg-white rounded-2xl border border-femcare-border overflow-x-auto" data-testid="pub-conversions-table">
            <table className="w-full text-sm">
                <thead className="bg-femcare-cream/40 text-femcare-ink/70 text-xs uppercase tracking-wider">
                    <tr className="text-left">
                        <th className="px-4 py-3">Date / Time</th>
                        <th className="px-4 py-3">Reference</th>
                        <th className="px-4 py-3">Currency</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Link</th>
                    </tr>
                </thead>
                <tbody>
                {rows.map((r, i) => (
                    <tr key={i} className="border-t border-femcare-border" data-testid={`pub-conv-row-${i}`}>
                        <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDate(r.at)}</td>
                        <td className="px-4 py-3 text-xs font-mono">{r.masked_payment_id}</td>
                        <td className="px-4 py-3">{r.currency}</td>
                        <td className="px-4 py-3">
                            <span className="inline-block rounded-full bg-emerald-50 text-emerald-800 px-2 py-0.5 text-xs font-semibold">Confirmed</span>
                        </td>
                        <td className="px-4 py-3 text-xs">{r.link_name || r.link_id || "—"}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
}


/**
 * CommissionCard — display-only "My Commission" summary.
 *
 * Numbers come from `/api/web/publisher/commission` which computes:
 *   monthly_count × payout_monthly + yearly_count × payout_yearly
 *   + lifetime_count × payout_lifetime
 * using the SAME confirmed-conversion predicate as the admin Web
 * Conversions dashboard. Currency + payout amounts are set by admin on the
 * publisher record — publishers cannot request a payout from this UI.
 */
function CommissionCard({ c }) {
    const fmt = (n) => new Intl.NumberFormat(c.currency === "INR" ? "en-IN" : "en-US", {
        style: "currency", currency: c.currency || "INR", maximumFractionDigits: 0,
    }).format(n || 0);
    return (
        <div className="rounded-2xl bg-white border border-femcare-border p-5" data-testid="pub-commission-card">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest text-femcare-plum font-semibold">My Commission</div>
                    <div className="text-3xl font-bold text-femcare-ink mt-1" data-testid="pub-commission-total">
                        {fmt(c.total)}
                    </div>
                    <div className="text-[11px] text-femcare-ink/50 mt-1">
                        Total earned from confirmed Razorpay conversions · currency {c.currency}
                    </div>
                </div>
                {!c.configured && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[11px] px-3 py-2 max-w-xs"
                         data-testid="pub-commission-unconfigured">
                        Femcare has not set your per-conversion payouts yet — please contact us.
                    </div>
                )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                <TierBox tier="Monthly"  count={c.counts.monthly}  payout={c.payouts.monthly}  currency={c.currency} tid="pub-comm-monthly" />
                <TierBox tier="Yearly"   count={c.counts.yearly}   payout={c.payouts.yearly}   currency={c.currency} tid="pub-comm-yearly" />
                <TierBox tier="Lifetime" count={c.counts.lifetime} payout={c.payouts.lifetime} currency={c.currency} tid="pub-comm-lifetime" />
            </div>
        </div>
    );
}

function TierBox({ tier, count, payout, currency, tid }) {
    const fmt = (n) => new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
        style: "currency", currency: currency || "INR", maximumFractionDigits: 0,
    }).format(n || 0);
    return (
        <div className="rounded-xl bg-femcare-cream/30 border border-femcare-border p-4" data-testid={tid}>
            <div className="text-xs uppercase tracking-wider text-femcare-ink/55">{tier}</div>
            <div className="text-2xl font-bold text-femcare-plum mt-1" data-testid={`${tid}-count`}>{count ?? 0}</div>
            <div className="text-[11px] text-femcare-ink/60 mt-1">
                × {fmt(payout)} <span className="text-femcare-ink/40">per conversion</span>
            </div>
            <div className="text-xs font-semibold text-femcare-ink mt-2" data-testid={`${tid}-subtotal`}>
                = {fmt((count || 0) * (payout || 0))}
            </div>
        </div>
    );
}

/**
 * PublisherLinkCreator — inline form so publishers can mint their own UTM
 * tracking links. Server pins `owner_publisher_id` and `publisher_id` to
 * this publisher — the client cannot forge attribution to a different
 * publisher's `publisher_id`.
 */
function PublisherLinkCreator({ allowedPublisherIds, onCreated, onError }) {
    const API = process.env.REACT_APP_BACKEND_URL;
    const [form, setForm] = useState({
        link_name:    "",
        target_market:"",
        utm_source:   "",
        utm_medium:   "affiliate",
        utm_campaign: "",
        utm_content:  "",
        publisher_id: allowedPublisherIds[0] || "",
        pid:          "",
        destination_kind: "home",
        destination_url:  "",
    });
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/web/publisher/links`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || "Create failed");
            setPreview(data.link);
            setTimeout(()=>{ onCreated && onCreated(); }, 1200);
        } catch (err) {
            onError && onError(err.message);
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="rounded-2xl bg-white border border-femcare-border p-5 space-y-4"
              data-testid="pub-create-link-form">
            <div className="text-sm font-semibold text-femcare-plum">Create UTM Link</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <F label="Link Name"       value={form.link_name}    onChange={v=>setForm({...form, link_name:v})}    tid="pub-l-name" />
                <F label="Target Market"   value={form.target_market} onChange={v=>setForm({...form, target_market:v})} tid="pub-l-market" />
                <F label="utm_source *"    value={form.utm_source}   onChange={v=>setForm({...form, utm_source:v})}   required tid="pub-l-source" />
                <F label="utm_medium"      value={form.utm_medium}   onChange={v=>setForm({...form, utm_medium:v})}   tid="pub-l-medium" />
                <F label="utm_campaign *"  value={form.utm_campaign} onChange={v=>setForm({...form, utm_campaign:v})} required tid="pub-l-campaign" />
                <F label="utm_content"     value={form.utm_content}  onChange={v=>setForm({...form, utm_content:v})}  tid="pub-l-content" />
                {allowedPublisherIds.length > 1 ? (
                    <label className="block">
                        <div className="text-xs uppercase tracking-wider text-femcare-ink/60 mb-1">publisher_id</div>
                        <select value={form.publisher_id} onChange={(e)=>setForm({...form, publisher_id:e.target.value})}
                                className="w-full rounded-xl border border-femcare-border px-3 py-2 text-sm"
                                data-testid="pub-l-pubid">
                            {allowedPublisherIds.map(pid => <option key={pid} value={pid}>{pid}</option>)}
                        </select>
                    </label>
                ) : (
                    <F label="publisher_id (locked to your assigned id)" value={form.publisher_id} onChange={()=>{}} disabled tid="pub-l-pubid" />
                )}
                <F label="pid"             value={form.pid}          onChange={v=>setForm({...form, pid:v})}          tid="pub-l-pid" />
                <label className="block md:col-span-2">
                    <div className="text-xs uppercase tracking-wider text-femcare-ink/60 mb-1">Destination</div>
                    <select value={form.destination_kind} onChange={(e)=>setForm({...form, destination_kind:e.target.value})}
                            className="w-full rounded-xl border border-femcare-border px-3 py-2 text-sm"
                            data-testid="pub-l-dest">
                        <option value="home">Homepage (femcare.life/)</option>
                        <option value="pricing">Pricing (femcare.life/pricing)</option>
                        <option value="signup">Signup (femcare.life/app/signup)</option>
                        <option value="daughter-care">Daughter Care (femcare.life/daughter-care)</option>
                    </select>
                </label>
            </div>
            {preview && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 p-3 text-xs break-all"
                     data-testid="pub-l-preview">
                    Created: <span className="font-mono">{preview.final_url}</span>
                </div>
            )}
            <div className="pt-2">
                <button type="submit" disabled={busy}
                        className="rounded-xl bg-femcare-plum text-white text-sm font-semibold px-5 py-2 disabled:opacity-40"
                        data-testid="pub-l-submit">
                    {busy ? "Creating…" : "Create Link"}
                </button>
            </div>
        </form>
    );
}

function F({ label, value, onChange, required, tid, disabled }) {
    return (
        <label className="block">
            <div className="text-xs uppercase tracking-wider text-femcare-ink/60 mb-1">{label}</div>
            <input type="text" required={required} value={value} disabled={disabled}
                   onChange={(e)=>onChange(e.target.value)}
                   className="w-full rounded-xl border border-femcare-border px-3 py-2 text-sm disabled:bg-femcare-cream/40 disabled:text-femcare-ink/60"
                   data-testid={tid} />
        </label>
    );
}

